// AI invitation designer — client helpers, vibe presets, and a Google-Fonts
// loader. The backend (server.py) picks fonts from a curated, Georgian-safe set
// and we load them on demand here. A Georgian fallback is always appended to the
// CSS font stack so ქართული text renders correctly whatever the AI chooses.

import axios from 'axios'
import { getApiUrl } from './utils'

const API = getApiUrl()

// Vibe ids must match AI_VIBE_PRESETS in backend/server.py.
export const VIBES = [
  { id: 'elegant',  emoji: '🕊️', label: { en: 'Elegant',  ka: 'ელეგანტური' } },
  { id: 'romantic', emoji: '💗', label: { en: 'Romantic', ka: 'რომანტიული' } },
  { id: 'playful',  emoji: '🎉', label: { en: 'Playful',  ka: 'მხიარული' } },
  { id: 'minimal',  emoji: '◻️', label: { en: 'Minimal',  ka: 'მინიმალური' } },
  { id: 'bold',     emoji: '⚡', label: { en: 'Bold',     ka: 'თამამი' } },
  { id: 'festive',  emoji: '🎊', label: { en: 'Festive',  ka: 'სადღესასწაულო' } },
  { id: 'luxury',   emoji: '👑', label: { en: 'Luxury',   ka: 'ლუქსი' } },
  { id: 'dreamy',   emoji: '🌙', label: { en: 'Dreamy',   ka: 'ნაზი' } },
]

// Weight axis fragment per Google-Fonts family. Single-weight faces get '' (no
// axis) so the css2 request never 404s; variable faces request real weights.
const FONT_AXES: Record<string, string> = {
  'Playfair Display': ':wght@400;500;600;700;800',
  'Cormorant Garamond': ':wght@400;500;600;700',
  'Fraunces': ':opsz,wght@9..144,400..700',
  'DM Serif Display': '',
  'Marcellus': '',
  'Cinzel': ':wght@400;500;600;700',
  'Italiana': '',
  'Abril Fatface': '',
  'Bricolage Grotesque': ':opsz,wght@12..96,400..800',
  'Space Grotesk': ':wght@400;500;600;700',
  'Sora': ':wght@400;500;600;700;800',
  'Unbounded': ':wght@400;500;600;700;800',
  'Inter': ':wght@400;500;600;700',
  'Manrope': ':wght@400;500;600;700;800',
  'DM Sans': ':opsz,wght@9..40,400..700',
  'Work Sans': ':wght@400;500;600;700',
  'Nunito Sans': ':wght@400;500;600;700',
  'Mulish': ':wght@400;500;600;700;800',
  'Karla': ':wght@400;500;600;700',
}

const loaded = new Set<string>()

// Load a font family from Google Fonts (once). Each family gets its own <link>
// so a single unavailable face can't break the others.
export function loadFont(family?: string | null) {
  if (!family || loaded.has(family) || typeof document === 'undefined') return
  loaded.add(family)
  const axis = FONT_AXES[family] ?? ':wght@400;500;600;700'
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family,
  ).replace(/%20/g, '+')}${axis}&display=swap`
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.aiFont = family
  document.head.appendChild(link)
}

export function loadFonts(families: Array<string | null | undefined>) {
  families.forEach(loadFont)
}

// The Georgian face that matches each family the AI may pick (AI_HEADING_FONTS /
// AI_BODY_FONTS in backend/server.py). None of those carry Georgian glyphs, so
// without a match here every AI design set its ქართული in the same Noto
// whatever face it chose for the English beside it. index.css declares these.
const KA_COMPANION: Record<string, string> = {
  'Playfair Display': 'BPG Serif',
  'Cormorant Garamond': 'Geo Times',
  Fraunces: 'BPG Serif',
  'DM Serif Display': 'BPG Serif',
  Marcellus: 'Geo Times',
  Cinzel: 'GL Kupiura',
  Italiana: 'Geo Times',
  'Abril Fatface': 'Geo Picasso',
  'Bricolage Grotesque': 'Mersad',
  'Space Grotesk': 'Eurostile Geo',
  Sora: 'Mersad',
  Unbounded: 'Eurostile Geo',
  Inter: 'Mersad',
  Manrope: 'Mersad',
  'DM Sans': 'Mersad',
  'Work Sans': 'Mersad',
  'Nunito Sans': 'ALK Rounded',
  Mulish: 'Mersad',
  Karla: 'Mersad',
}

// CSS font-family stack: chosen face → matching Georgian face → Noto backstop →
// generic. Noto stays in the stack because it is the only Georgian family here
// that covers every block, so an unmatched face still renders ქართული.
export function fontStack(family?: string | null, kind: 'heading' | 'body' = 'body') {
  if (!family) return ''
  const noto =
    kind === 'heading'
      ? `'Noto Serif Georgian', 'Noto Sans Georgian'`
      : `'Noto Sans Georgian', 'Noto Serif Georgian'`
  // Own-property only: `family` is free-form host input, and a plain lookup
  // would resolve inherited keys like `constructor` to a function.
  const companion = Object.prototype.hasOwnProperty.call(KA_COMPANION, family)
    ? KA_COMPANION[family]
    : undefined
  const geo = companion ? `'${companion}', ${noto}` : noto
  const generic = kind === 'heading' ? 'serif' : 'sans-serif'
  return `'${family}', ${geo}, system-ui, ${generic}`
}

// ---- API ----

export async function getAiQuota() {
  const res = await axios.get(`${API}/ai/design/quota`, { withCredentials: true })
  return res.data
}

export async function generateAiDesign(payload: {
  title?: string
  event_type?: string
  language?: string
  vibe?: string
  prompt?: string
  event_id?: string
}) {
  const res = await axios.post(`${API}/ai/design/generate`, payload, {
    withCredentials: true,
  })
  return res.data
}

export async function purchaseAiCredits(quantity = 1) {
  const res = await axios.post(
    `${API}/ai/design/purchase`,
    { quantity },
    { withCredentials: true },
  )
  return res.data
}
